import { Capacitor } from "@capacitor/core";
import { useCallback, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { useQRScanner } from "@/components/global/qr-scanner/hooks/use-qr-scanner";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { api } from "@/lib/backend";
import { type EmployeeSettableResponse, type Shift } from "@/lib/backend/api";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { TRAVEL_EXPENSE_TYPE, type TravelExpenseType } from "@/lib/constants";
import { queryOptions_signed_key_project } from "@/lib/requests/file/file.get-signed-key";
import { canRespondToHours } from "@/lib/shift/employee-hours-response";
import { useSelectorModalStore } from "@/lib/store/modal";
import { logger } from "@/logger";
import { formatTime } from "@/utils/format-datetime";
import {
  durationToMinutes,
  formatDurationToTimeString,
  minutesToDuration,
  parseTimeString,
} from "@/utils/time";

/**
 * State machine for completed shift card actions
 *
 * Uses reducer pattern to manage complex state transitions:
 *
 * - Hours registration (manual or QR-based)
 * - Travel expense declarations
 * - Modal states
 * - Loading states
 */

// ==================== STATE TYPES ====================

type ModalState = "closed" | "hours" | "expenses" | "declaration-preview";

interface ShiftActionsState {
  activeModal: ModalState;
  isSubmittingHours: boolean;
  isSubmittingExpenses: boolean;
  lastAction: string | null;
}

type ShiftActionsAction =
  | { type: "OPEN_HOURS_MODAL" }
  | { type: "OPEN_EXPENSES_MODAL" }
  | { type: "OPEN_DECLARATION_PREVIEW_MODAL" }
  | { type: "CLOSE_MODAL" }
  | { type: "START_HOURS_SUBMISSION" }
  | { type: "HOURS_SUBMISSION_SUCCESS" }
  | { type: "HOURS_SUBMISSION_ERROR" }
  | { type: "START_EXPENSES_SUBMISSION" }
  | { type: "EXPENSES_SUBMISSION_SUCCESS" }
  | { type: "EXPENSES_SUBMISSION_ERROR" }
  | { type: "QR_CLOCK_IN_START" }
  | { type: "QR_CLOCK_OUT_START" }
  | { type: "QR_CLOCK_SUCCESS" }
  | { type: "QR_CLOCK_ERROR" };

// ==================== REDUCER ====================

function shiftActionsReducer(
  state: ShiftActionsState,
  action: ShiftActionsAction,
): ShiftActionsState {
  switch (action.type) {
    case "OPEN_HOURS_MODAL":
      return { ...state, activeModal: "hours" };

    case "OPEN_EXPENSES_MODAL":
      return { ...state, activeModal: "expenses" };

    case "OPEN_DECLARATION_PREVIEW_MODAL":
      return { ...state, activeModal: "declaration-preview" };

    case "CLOSE_MODAL":
      return { ...state, activeModal: "closed" };

    case "START_HOURS_SUBMISSION":
      return { ...state, isSubmittingHours: true, lastAction: "hours" };

    case "HOURS_SUBMISSION_SUCCESS":
      return {
        ...state,
        isSubmittingHours: false,
        activeModal: "closed",
        lastAction: null,
      };

    case "HOURS_SUBMISSION_ERROR":
      return { ...state, isSubmittingHours: false };

    case "START_EXPENSES_SUBMISSION":
      return { ...state, isSubmittingExpenses: true, lastAction: "expenses" };

    case "EXPENSES_SUBMISSION_SUCCESS":
      return {
        ...state,
        isSubmittingExpenses: false,
        activeModal: "closed",
        lastAction: null,
      };

    case "EXPENSES_SUBMISSION_ERROR":
      return { ...state, isSubmittingExpenses: false };

    case "QR_CLOCK_IN_START":
    case "QR_CLOCK_OUT_START":
      return { ...state, lastAction: "qr-clock" };

    case "QR_CLOCK_SUCCESS":
    case "QR_CLOCK_ERROR":
      return { ...state, lastAction: null };

    default:
      return state;
  }
}

const initialState: ShiftActionsState = {
  activeModal: "closed",
  isSubmittingHours: false,
  isSubmittingExpenses: false,
  lastAction: null,
};

// ==================== HOOK ====================

export function useCompletedShiftActions(shift: Shift) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const presentModal = useSelectorModalStore.use.present();
  const validator = useValidator();

  // State machine
  const [state, dispatch] = useReducer(shiftActionsReducer, initialState);

  // Modal refs for imperative control
  const modalHoursRef = useRef<HTMLIonModalElement>(null);
  const modalExpensesRef = useRef<HTMLIonModalElement>(null);
  const modalDeclarationPreviewRef = useRef<HTMLIonModalElement>(null);

  // ==================== DATA FETCHING ====================

  const {
    data: expenseClaim,
    isLoading: isLoadingExpenseClaim,
    isError: isErrorExpenseClaim,
  } = useQuery({
    queryKey: ["shift", shift.id, "expense-claims", shift.shiftTimeEntry?.id],
    queryFn: () => {
      const shiftTimeEntryId = shift.shiftTimeEntry?.id;
      if (!shiftTimeEntryId) {
        return null;
      }
      return extractionWrapper(api.expenseClaim.getById(shiftTimeEntryId));
    },
    enabled: !!shift.shiftTimeEntry?.id,
  });

  // Primary file key for the inline preview: prefer the legacy single fileLink,
  // otherwise fall back to the first attachment (new claims have no fileLink).
  const link = expenseClaim?.fileLink;
  const primaryFileKey: string | null =
    typeof link === "string" && link
      ? link
      : link && typeof link === "object" && "key" in link
        ? (link as { key: string }).key
        : (expenseClaim?.attachments?.[0]?.url ?? null);

  // Fetch signed URL for the primary expense claim file
  const { data: expenseClaimFileData } = useQuery({
    queryKey: ["expense-claim-file", shift.projectId, expenseClaim?.id, primaryFileKey],
    queryFn: async () => {
      if (!shift.projectId || !expenseClaim?.id || !primaryFileKey) {
        return null;
      }
      return extractionWrapper(
        api.file.getExpenseClaimUrl(shift.projectId, expenseClaim.id, primaryFileKey),
      );
    },
    enabled: !!shift.projectId && !!expenseClaim?.id && !!primaryFileKey,
    staleTime: 1000 * 60 * 5, // 5 minutes - signed URLs typically expire after some time
  });

  const expenseClaimFileUrl = expenseClaimFileData?.url;

  // ==================== PERMISSIONS ====================

  const permissions = {
    isProjectFinalized: shift.project?.status === "FINALIZED",
    allowEmployeeSubmitHours: !!shift.project?.allowEmployeeSubmitHours,
    allowTravelExpenses: !!shift.project?.allowEmployeeSubmitDeclarations,
    enableQRCodeClocking: !!shift.project?.enableQRCodeClocking,
  };

  const canSubmitHours = !permissions.isProjectFinalized && permissions.allowEmployeeSubmitHours;
  const canSubmitDeclarations = !permissions.isProjectFinalized && permissions.allowTravelExpenses;

  /**
   * Mag de medewerker reageren op uren die het kantoor namens hem invulde (admin#1093)?
   *
   * Dit zat achter `canSubmitHours`, en dus achter `allowEmployeeSubmitHours`. Die schakelaar staat
   * bij Jongens van de Bar in 404 van de 426 projecten uit en bij HOP Producties in 60 van de 60
   * (gemeten op productie 28-09-2026), en dat zijn precies de klanten waar de melding vandaan komt.
   * Daar rendeerde de hele urenregel niet: geen uren te zien, geen Klopt/Klopt niet, niets om op te
   * tikken.
   *
   * Zelf registreren en reageren op andermans invoer zijn twee dingen. Deze vraag staat daarom los,
   * en `isReadOnly` hieronder houdt de weg naar het invoerformulier dicht: de medewerker kijkt en
   * reageert, hij vult niets in.
   *
   * Een afgerond project blijft wel dicht. De api weigert de reactie daar niet, maar administratief
   * is de zaak klaar en een knop die niets meer verandert is een belofte die niet waargemaakt
   * wordt.
   */
  const canRespondToOfficeHours =
    !permissions.isProjectFinalized && canRespondToHours(shift.shiftTimeEntry);

  // ==================== CLOCK STATUS ====================

  const clockStatus = {
    // Check if shift has a time entry ID (meaning a clock-in/hours entry exists)
    hasShiftTimeEntry: !!shift.shiftTimeEntry?.id,
    // Derive clock state from the entry's ACTUAL start/end times, not its id.
    // A QR clock-in creates an entry with startAt but no endAt yet, so keying
    // these off the id made isClockedIn always false and hid the clock-out scan
    // once a shift's scheduled end time had passed (employee could not clock out).
    hasStartTime: !!shift.shiftTimeEntry?.startAt,
    hasEndTime: !!shift.shiftTimeEntry?.endAt,
    get isClockedIn() {
      return this.hasStartTime && !this.hasEndTime;
    },
    get isClockedOut() {
      // Clocked out only once an end time is recorded on the time entry
      return this.hasEndTime;
    },
  };

  // ==================== INVALIDATE QUERIES HELPER ====================

  const invalidateShiftData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["shifts"] });
    queryClient.invalidateQueries({ queryKey: ["shift", shift.id] });
    queryClient.invalidateQueries({
      queryKey: ["shift", shift.id, "expense-claims"],
    });
  }, [queryClient, shift.id]);

  // ==================== HOUR REGISTRATION ====================

  const hoursMutation = useMutation({
    mutationKey: ["shift", shift.id, "hours"],
    mutationFn: async (variables: {
      startTime: string;
      endTime: string;
      breakTime: number;
      comment?: string;
    }) => {
      dispatch({ type: "START_HOURS_SUBMISSION" });
      // Free-text remark (issue #426): trim, and only send the field when the
      // employee actually typed something — empty/whitespace becomes null so
      // it does not render a (false) badge in Planning.
      const trimmedComment = variables.comment?.trim() ?? "";
      await api.shiftDeclaration.updateFullEntry(shift.id, {
        startAt: variables.startTime,
        endAt: variables.endTime,
        breakTime: variables.breakTime,
        shiftId: shift.id,
        comment: trimmedComment === "" ? null : trimmedComment,
      });
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-hours`);
    },
    onSuccess: () => {
      toast.success(t("actions:declare-hours.success"), {
        id: `shift-${shift.id}-hours`,
      });
      invalidateShiftData();
      dispatch({ type: "HOURS_SUBMISSION_SUCCESS" });
      // Close modal imperatively
      setTimeout(() => {
        modalHoursRef.current?.dismiss();
      }, 100);

      // Submitting hours settles the shift, so it leaves the action list and this row unmounts
      // with it — which is exactly the moment travel costs get forgotten, and they are the part
      // of a shift an employee loses money on by forgetting. Ask now, from the modal store so the
      // question survives the row that raised it, and only where there is something to ask:
      // declaring has to be allowed for this project, and nothing claimed yet.
      if (canSubmitDeclarations && !expenseClaim) {
        setTimeout(() => {
          presentModal("declarationPrompt", { shiftId: shift.id });
        }, 400);
      }
    },
    onError: (error) => {
      toast.error(t("actions:declare-hours.error"), {
        id: `shift-${shift.id}-hours`,
      });
      logger.error(error);
      dispatch({ type: "HOURS_SUBMISSION_ERROR" });
    },
  });

  /**
   * Reactie op door de organisatie geüploade uren (admin#624): "klopt" of "klopt niet" met
   * verplichte toelichting.
   *
   * Dit muteert de uren-waarden NIET — de medewerker signaleert, de planner corrigeert. Daarom ook
   * een eigen mutatie naast `hoursMutation` en niet een extra veld daarop: die schrijft
   * start/eind/pauze.
   */
  const employeeResponseMutation = useMutation({
    mutationKey: ["shift", shift.id, "employee-response"],
    mutationFn: async (variables: { response: "AGREED" | "DISPUTED"; note?: string }) => {
      const trimmedNote = variables.note?.trim() ?? "";
      if (variables.response === "DISPUTED" && trimmedNote === "") {
        // De API weigert dit met een 400; hier vroeg falen scheelt de
        // gebruiker een onbegrijpelijke foutmelding.
        throw new Error(t("actions:hours-response.note-required"));
      }
      await api.shiftDeclaration.respondToUploadedHours(shift.id, {
        response: variables.response as EmployeeSettableResponse,
        note: variables.response === "DISPUTED" ? trimmedNote : null,
      });
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-employee-response`);
    },
    onSuccess: (_data, variables) => {
      toast.success(
        variables.response === "AGREED"
          ? t("actions:hours-response.success-agreed")
          : t("actions:hours-response.success-disputed"),
        { id: `shift-${shift.id}-employee-response` },
      );
      invalidateShiftData();
    },
    onError: (error) => {
      toast.error(t("actions:hours-response.error"), {
        id: `shift-${shift.id}-employee-response`,
      });
      logger.error(error);
    },
  });

  const qrClockInMutation = useMutation({
    mutationKey: ["shift", shift.id, "qr-clock-in"],
    mutationFn: async (qrCodeData: string) => {
      dispatch({ type: "QR_CLOCK_IN_START" });
      if (!shift.project?.id) {
        throw new Error("Project ID is required");
      }
      return await extractionWrapper(
        api.shiftDeclaration.clockIn({
          projectId: shift.project.id,
          qrCodeData,
          shiftId: shift.id,
        }),
      );
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-clock`);
    },
    onSuccess: () => {
      toast.success(t("shift:clock-in.success"), {
        id: `shift-${shift.id}-clock`,
      });
      invalidateShiftData();
      dispatch({ type: "QR_CLOCK_SUCCESS" });
    },
    onError: (error: any) => {
      const message = error?.message || t("shift:clock-in.error");
      toast.error(message, { id: `shift-${shift.id}-clock` });
      logger.error("QR clock-in error", error);
      dispatch({ type: "QR_CLOCK_ERROR" });
    },
  });

  const qrClockOutMutation = useMutation({
    mutationKey: ["shift", shift.id, "qr-clock-out"],
    mutationFn: async (qrCodeData: string) => {
      dispatch({ type: "QR_CLOCK_OUT_START" });
      if (!shift.project?.id) {
        throw new Error("Project ID is required");
      }
      return await extractionWrapper(
        api.shiftDeclaration.clockOut({
          projectId: shift.project.id,
          qrCodeData,
          shiftId: shift.id,
        }),
      );
    },
    onMutate: () => {
      // Geen toast.loading — zie `sonner.guard.test.ts`. De knop toont de voortgang al.
      toast.dismiss(`shift-${shift.id}-clock`);
    },
    onSuccess: () => {
      toast.success(t("shift:clock-out.success"), {
        id: `shift-${shift.id}-clock`,
      });
      invalidateShiftData();
      dispatch({ type: "QR_CLOCK_SUCCESS" });
    },
    onError: (error: any) => {
      const message = error?.message || t("shift:clock-out.error");
      toast.error(message, { id: `shift-${shift.id}-clock` });
      logger.error("QR clock-out error", error);
      dispatch({ type: "QR_CLOCK_ERROR" });
    },
  });

  const {
    scanQRCode: scanClockIn,
    isScanning: isScanningClockIn,
    WebScannerComponent: ClockInWebScanner,
  } = useQRScanner({
    onScanSuccess: (data) => qrClockInMutation.mutate(data),
  });

  const {
    scanQRCode: scanClockOut,
    isScanning: isScanningClockOut,
    WebScannerComponent: ClockOutWebScanner,
  } = useQRScanner({
    onScanSuccess: (data) => qrClockOutMutation.mutate(data),
  });

  const timeZone = shift.timeZone ?? shift.project?.timeZone;

  const existingStartAt =
    (typeof shift.shiftTimeEntry?.startAt === "string"
      ? shift.shiftTimeEntry.startAt
      : undefined) ?? shift.startAt;

  const existingEndAt =
    (typeof shift.shiftTimeEntry?.endAt === "string" ? shift.shiftTimeEntry.endAt : undefined) ??
    shift.endAt;

  const [formHours, { Element: FormElementHours }] = useBoomerForm({
    defaultValues: {
      // De API krijgt een kale "HH:mm" en legt die naast de tijdzone van het
      // project. De voorinvulling moet dus óók in projecttijd staan: tempo's
      // `format()` las de klok van het toestel, zodat een dienst van 15:00 op
      // een telefoon in Toronto als 09:00 klaarstond — één tik van een
      // verkeerde urenregistratie af.
      // If editing existing hours, use time entry data; otherwise use planned shift times
      startTime: existingStartAt ? formatTime(existingStartAt, { timeZone }) : "",
      endTime: existingEndAt ? formatTime(existingEndAt, { timeZone }) : "",
      breakDuration: formatDurationToTimeString(
        minutesToDuration(shift.shiftTimeEntry?.breakTimeMinutes ?? 0),
      ),
      // Optional free-text remark (issue #426); prefilled when re-editing.
      comment: shift.shiftTimeEntry?.comment ?? "",
    },
    onSubmit: async ({ value }) => {
      await hoursMutation.mutateAsync({
        startTime: value.startTime,
        endTime: value.endTime,
        breakTime: durationToMinutes(parseTimeString(value.breakDuration)),
        comment: value.comment,
      });
    },
  });

  // ==================== TRAVEL EXPENSES ====================

  const travelExpensesMutation = useMutation({
    mutationKey: ["shift", shift.id, "travel-expenses"],
    mutationFn: async (variables: {
      types: TravelExpenseType[];
      files: File[];
      fileDescriptions?: string[];
      // A declaration is EITHER a euro amount OR a kilometer distance (XOR) — never both.
      unit: "EUROS" | "KILOMETERS";
      cost: number | null;
      kilometers: number | null;
      note: string;
    }) => {
      dispatch({ type: "START_EXPENSES_SUBMISSION" });

      const isKilometers = variables.unit === "KILOMETERS";

      // Validation - DEFENSIVE: Prevent crashes from undefined array access
      // Even though form validation should catch these, we add defensive checks
      if (!variables.types || variables.types.length === 0) {
        throw new Error(t("actions:declare-travel-expenses.type-required"));
      }
      if (!variables.files || variables.files.length === 0) {
        throw new Error(t("actions:declare-travel-expenses.attachment-required"));
      }
      if (variables.files.length > 5) {
        throw new Error(t("actions:declare-travel-expenses.attachment-max", { max: 5 }));
      }
      // XOR guard: exactly the field matching the selected unit must be present and positive.
      // The server (re)computes euros for km declarations, so the app never sends both.
      if (isKilometers) {
        if (variables.kilometers === null || variables.kilometers <= 0) {
          throw new Error(t("actions:declare-travel-expenses.kilometers-required"));
        }
      } else if (variables.cost === null || variables.cost <= 0) {
        throw new Error(t("actions:declare-travel-expenses.amount-required"));
      }
      // Every entry must be a real, non-empty File object
      if (!variables.files.every((file) => file instanceof File && file.size > 0)) {
        throw new Error(t("actions:declare-travel-expenses.file-invalid"));
      }

      const expenseType = variables.types[0]?.toUpperCase() as "CAR" | "PUBLIC_TRANSPORT" | "OTHER";

      if (!expenseType) {
        throw new Error(t("actions:declare-travel-expenses.missing-data"));
      }

      // Keyed on the SHIFT, not on an hours line. Submitting a receipt says
      // nothing about the hours worked, so the server creates an empty hours
      // line when there is none (#495). We used to force one into existence
      // here by submitting the shift's *planned* times through
      // `updateFullEntry` — which stored those as if they were the worked
      // hours, locked the employee out of correcting them, and had them
      // recorded as the employee's own claim about their hours.
      //
      // Create the claim first (no document), then attach all files in one
      // request so a declaration can carry up to 5 attachments.
      // XOR: send EXACTLY ONE of `amount` / `kilometers`. For km the server derives the euros
      // from the km-rate, so we must NOT compute or send an amount here.
      const claim = await extractionWrapper(
        api.expenseClaim.createForShift(shift.id, {
          type: expenseType,
          ...(isKilometers
            ? { kilometers: variables.kilometers ?? undefined }
            : { amount: variables.cost ?? undefined }),
          note: variables.note,
          status: "PENDING",
        }),
      );

      await extractionWrapper(
        api.expenseClaim.addAttachments(claim.id, {
          documents: variables.files,
          descriptions: JSON.stringify(variables.fileDescriptions ?? []),
        }),
      );

      // Non-blocking km declaration (#555): the submit ALWAYS succeeds now. When
      // no km-rate is configured (relation nor org) the server stores the claim
      // UNPRICED — kilometers kept, no `kmRate`/amount yet. Detect that so the UI
      // can show a soft "bedrag volgt zodra het tarief is ingesteld" notice
      // instead of a hard error. A priced km claim comes back with a `kmRate`.
      return { isUnpricedKm: isKilometers && claim?.kmRate == null };
    },
    onMutate: () => {
      // CRITICAL FIX: Don't use toast.loading() - it causes a white overlay bug on iOS
      // See: docs/common-issues.md (Sonner v1.7.4 bug - GitHub issues #476, #401)
      // The backdrop created by toast.loading() doesn't properly dismiss on iOS
      toast.dismiss(`shift-${shift.id}-expenses`);
    },
    onSuccess: (result) => {
      // A km declaration submitted while no km-rate is configured is stored
      // UNPRICED (#555). The submit still succeeded, so surface a soft,
      // informational notice ("het bedrag volgt zodra het tarief is ingesteld")
      // rather than the plain success — never a blocking error.
      toast.success(
        result?.isUnpricedKm
          ? t("actions:declare-travel-expenses.unpriced-km")
          : t("actions:declare-travel-expenses.success"),
        {
          id: `shift-${shift.id}-expenses`,
        },
      );

      // iOS FIX: Add delay before query invalidation
      // iOS needs time for the native file picker to fully dismiss
      // See: docs/common-issues.md - 300ms delay recommended for iOS
      const delay = Capacitor.getPlatform() === "ios" ? 300 : 0;
      setTimeout(() => {
        invalidateShiftData();
      }, delay);

      dispatch({ type: "EXPENSES_SUBMISSION_SUCCESS" });

      // Close modal imperatively
      setTimeout(() => {
        modalExpensesRef.current?.dismiss();
      }, 100);
    },
    onError: (error) => {
      // A missing km-rate is no longer an error (#555): the server stores the
      // claim unpriced and the mutation resolves successfully. Any error that
      // reaches here is a genuine failure (network, attachments, …), so show the
      // generic message.
      toast.error(t("actions:declare-travel-expenses.error"), {
        id: `shift-${shift.id}-expenses`,
      });
      logger.error(error);
      dispatch({ type: "EXPENSES_SUBMISSION_ERROR" });

      // ERROR RECOVERY: Reset form to clear stale file references
      formTravelExpenses.reset();
    },
  });

  const [formTravelExpenses, { Element: FormElementExpenses }] = useBoomerForm({
    defaultValues: {
      types: [],
      files: [],
      fileDescriptions: [],
      // Unit toggle: a declaration is EITHER a euro amount OR a km distance (XOR). Default euros.
      unit: "EUROS",
      cost: null,
      kilometers: null,
      note: "",
    } as {
      types: TravelExpenseType[];
      files: File[];
      fileDescriptions: string[];
      unit: "EUROS" | "KILOMETERS";
      cost: number | null;
      kilometers: number | null;
      note: string;
    },
    onSubmit: async ({ value }) => {
      logger.info("Travel expenses form submitting", { value });
      try {
        await travelExpensesMutation.mutateAsync(value);
        logger.info("Travel expenses form submitted successfully");
      } catch (error) {
        // Error is already handled by the mutation's onError callback
        // Re-throw to prevent form submission from completing
        logger.error("Travel expenses form submission error:", error);
        throw error;
      }
    },
  });

  // ==================== UI STATE ====================

  const hourButtonState = {
    get text() {
      // Alleen kijken en reageren: dan geen "Inklokken" en geen "Uren aanpassen", want er gaat geen
      // formulier open. De regel heet dan naar wat erin staat.
      if (this.isReadOnly) {
        return t("shift:hours.title");
      }
      if (!permissions.enableQRCodeClocking) {
        // Manual entry mode - always allow editing
        return clockStatus.isClockedOut
          ? t("actions:declare-hours.edit")
          : t("actions:declare-hours.enter");
      }
      // QR mode - show status
      if (clockStatus.isClockedOut) {
        return t("actions:declare-hours.entered");
      }
      if (clockStatus.isClockedIn) {
        return "Uitklokken";
      }
      return "Inklokken";
    },
    get isVisible() {
      // Uren die het kantoor invulde horen op het scherm, ook zonder invoerrecht: anders is er
      // niets om op te tikken en zijn de Klopt/Klopt niet-knoppen onbereikbaar (admin#1093).
      if (canRespondToOfficeHours) {
        return true;
      }
      // QR code mode: hide button once clocked out (hours are finalized)
      if (permissions.enableQRCodeClocking) {
        return canSubmitHours && !clockStatus.isClockedOut;
      }
      // Manual entry mode: always show button to allow editing
      return canSubmitHours;
    },
    /**
     * De urenregel is er om te bekijken en op te reageren, niet om in te vullen.
     *
     * Eén waarheid voor de drie ingangen, zodat geen van hen een blauwe actieknop naast een regel
     * zet die geen formulier opent. `opensHoursPreview` stuurt de tik al naar de preview; dit houdt
     * het label erbij kloppend.
     */
    get isReadOnly() {
      return !canSubmitHours;
    },
    get isDisabled() {
      return (
        qrClockInMutation.isPending ||
        qrClockOutMutation.isPending ||
        isScanningClockIn ||
        isScanningClockOut
      );
    },
    get action() {
      if (permissions.enableQRCodeClocking) {
        if (clockStatus.isClockedIn) {
          return scanClockOut;
        }
        return scanClockIn;
      }
      return undefined; // Will trigger modal
    },
  };

  const declarationButtonState = {
    get text() {
      if (expenseClaim) {
        return t(`enums:travelExpenseEntryStatus.${expenseClaim.status}`);
      }
      return "Declaratie indienen";
    },
    get isVisible() {
      return canSubmitDeclarations;
    },
    get isDisabled() {
      // Always allow clicking - button will show preview if expense claim exists, or form if not
      return false;
    },
  };

  // ==================== ACTIONS ====================

  const openHoursModal = useCallback(() => {
    dispatch({ type: "OPEN_HOURS_MODAL" });
    setTimeout(() => {
      modalHoursRef.current?.present();
    }, 50);
  }, []);

  const openExpensesModal = useCallback(() => {
    dispatch({ type: "OPEN_EXPENSES_MODAL" });
    setTimeout(() => {
      modalExpensesRef.current?.present();
    }, 50);
  }, []);

  const openDeclarationPreview = useCallback(() => {
    dispatch({ type: "OPEN_DECLARATION_PREVIEW_MODAL" });
    setTimeout(() => {
      modalDeclarationPreviewRef.current?.present();
    }, 50);
  }, []);

  const closeModal = useCallback(() => {
    dispatch({ type: "CLOSE_MODAL" });
  }, []);

  return {
    // State
    state,

    // Data
    expenseClaim,
    expenseClaimFileUrl,
    isLoadingExpenseClaim,
    isErrorExpenseClaim,

    // Permissions
    permissions,
    canSubmitHours,
    canSubmitDeclarations,

    // Clock status
    clockStatus,

    // Hour registration
    hoursMutation,
    employeeResponseMutation,
    qrClockInMutation,
    qrClockOutMutation,
    formHours,
    FormElementHours,
    scanClockIn,
    scanClockOut,
    isScanningClockIn,
    isScanningClockOut,
    ClockInWebScanner,
    ClockOutWebScanner,

    // Travel expenses
    travelExpensesMutation,
    formTravelExpenses,
    FormElementExpenses,

    // UI state
    hourButtonState,
    declarationButtonState,

    // Actions
    openHoursModal,
    openExpensesModal,
    openDeclarationPreview,
    closeModal,

    // Refs
    modalHoursRef,
    modalExpensesRef,
    modalDeclarationPreviewRef,

    // Validators
    validator,
  };
}
